import React, { createContext, useEffect, useCallback, useState } from 'react';

import { createHook } from 'utils/utils';
import { sendToAnalyticsCustomEventObj } from 'lib/googleAnalytics';
import { CORS_DOMAINS } from 'data/constants';

const ChromeExtensionContext = createContext();

const useChromeExtension = () => createHook('useChromeExtension', ChromeExtensionContext);

export const EXTENSION_VERSION_UNKNOWN = '__unknown__';
export const EXTENSION_NOT_INSTALLED = '__not_installed__';
export const EXTENSION_INSTALLED = '__installed__';
export const EXTENSION_MIN_VERSION = [2, 0, 1]; // [major, minor, patch]

const ChromeExtensionContextProvider = ({ children }) => {
  const [extensionStatus, setExtensionStatus] = useState(EXTENSION_VERSION_UNKNOWN);
  const [isExtensionPinned, setExtensionPinned] = useState(true);
  const [extensionsVersions, setExtensionsVersions] = useState([]);
  // Current focused page content URL
  const [contentUrl, setContentUrl] = useState(null);

  useEffect(() => {
    const requestVersion = () => {
      const request = {
        what: 'CHROME_EXTENSION_CHECK_VERSION_REQUEST',
      };
      window.postMessage(request, '*');
    };

    const sendCorsDomains = () => {
      const request = {
        what: 'DOMAINS_FOR_CORS_OVERRIDE',
        domains: CORS_DOMAINS,
      };
      window.postMessage(request, '*');
    };
    // Request version after the page load
    // The delay is needed to wait until the content script is started
    setTimeout(requestVersion, 300);
    setTimeout(sendCorsDomains, 300);

    // If extensition didn't report its version within 400 msecs, consider that it's not installed
    setTimeout(() => {
      setExtensionStatus((oldExtensionStatus) =>
        oldExtensionStatus === EXTENSION_VERSION_UNKNOWN
          ? EXTENSION_NOT_INSTALLED
          : oldExtensionStatus,
      );
    }, 400);
  }, []);

  useEffect(() => {
    const requestIsExtensionPinned = () => {
      const request = {
        what: 'CHROME_EXTENSION_CHECK_IS_PINNED_REQUEST',
      };
      window.postMessage(request, '*');
    };
    // Request version after the page load
    // The delay is needed to wait until the content script is started
    setTimeout(requestIsExtensionPinned, 300);
    // If extensition didn't report its version within 400 msecs, consider that it's not installed
    setTimeout(
      () => setExtensionPinned((oldPinned) => (oldPinned === undefined ? true : oldPinned)),
      400,
    );
  }, []);

  const onMessageHandler = useCallback((event) => {
    const { data } = event;
    const { payload, what } = data;
    switch (what) {
      case 'CHROME_EXTENSION_VERSION':
        const { version } = payload;
        setExtensionStatus(EXTENSION_INSTALLED);
        setExtensionsVersions((prev) => (prev.includes(version) ? prev : [...prev, version]));
        break;
      case 'CHROME_EXTENSION_IS_PINNED':
        setExtensionPinned(payload.isPinned);
        break;
      case 'CHROME_EXTENSION_CONTENT_URL':
        setContentUrl(payload.url);
        break;
      case 'VIDEO_SEEKED':
        sendToAnalyticsCustomEventObj('video_seeked', {
          current_time: payload.currentTime,
          duration: payload.duration,
          url: payload.url,
        });
        break;
      default:
        break;
    }
  }, []);

  useEffect(() => {
    window.addEventListener('message', onMessageHandler);
    return () => {
      window.removeEventListener('message', onMessageHandler);
    };
  }, [onMessageHandler]);

  const isExtensionInstalled = extensionStatus !== EXTENSION_NOT_INSTALLED;

  const isExtensionUpToDate = () => {
    if (
      extensionStatus === EXTENSION_VERSION_UNKNOWN ||
      extensionStatus === EXTENSION_NOT_INSTALLED ||
      extensionsVersions.length > 1
    ) {
      return false;
    }
    const parts = extensionsVersions[0].split('.');
    for (let i = 0; i < parts.length; i++) {
      if (parts[i] < EXTENSION_MIN_VERSION[i]) {
        return false;
      } else if (parts[i] > EXTENSION_MIN_VERSION[i]) {
        return true;
      }
    }
    return true;
  };

  const checkExtensionVersion = (extensionVersion) => {
    const parts = extensionVersion.split('.');
    for (let i = 0; i < parts.length; i++) {
      if (parts[i] < EXTENSION_MIN_VERSION[i]) {
        return false;
      } else if (parts[i] > EXTENSION_MIN_VERSION[i]) {
        return true;
      }
    }
    return true;
  };

  const hasOldExtension = () => {
    return extensionsVersions.some((v) => v.startsWith('1.'));
  };

  const hasOldAndNewExtensionTogether = () => {
    if (extensionsVersions.length === 1) return false;

    // Check for at least one version starting with '1.' and one with '2.'
    const hasV1 = extensionsVersions.some((v) => v.startsWith('1.'));
    const hasV2 = extensionsVersions.some((v) => v.startsWith('2.'));
    if (hasV1 && hasV2) {
      return true;
    }
    return false;
  };

  return (
    <ChromeExtensionContext.Provider
      value={{
        extensionsVersions,
        isExtensionInstalled,
        isExtensionUpToDate,
        isExtensionPinned,
        hasOldAndNewExtensionTogether,
        hasOldExtension,
        contentUrl,
        checkExtensionVersion,
      }}
    >
      {children}
    </ChromeExtensionContext.Provider>
  );
};

export { ChromeExtensionContextProvider, useChromeExtension };
